import {CallTrackingResponse, Swap} from '../../types';
import {DniCache, CallTrackingRepository, PageContext} from '../app';
import {error} from '../debugging';
import {Api} from "./remote";

export const SESSION_KEY = '800cltrk.session';
const SESSION_DURATION = 60 * 30 * 24 * 365; // one year
const SESSION_DURATION_MS = SESSION_DURATION * 1000; // one year
const SWAP_DURATION_MS = 60 * 30 * 1000; // 30 minutes

type CallTrackingOptions = {
    storage: DniCache,
    apiUrl: string,
    pageContext: PageContext,
}

export type CachedCallTrackingResponse = {
    sessionId: string;
    lastOverSwappedAt?: string | null;
    swaps: Swap[]
}

const expireSwaps = (options: CallTrackingOptions): void => {
    // SESSION_KEY is cached for one year, and expiry is reset whenever there's activity.
    // but we only want swaps to be cached for up to 30 minutes of inactivity.

    // expiration date timestamp (ms)
    // this should be `last_activity_at + 1 year`
    const expiry = options.storage.getExpiry(SESSION_KEY);

    if (expiry === null) {
        return;
    }

    if (expiry - SESSION_DURATION_MS + SWAP_DURATION_MS < (new Date()).getTime()) {
        const data = options.storage.get<CachedCallTrackingResponse>(SESSION_KEY) || {};

        // this resets the TTL, but that's fine since we only execute this right before refreshing the
        // cached data anyway.
        options.storage.set(SESSION_KEY, {
            ...data,
            swaps: undefined,
        }, SESSION_DURATION);
    }
};

export function initCallTracking(api: Api, options: CallTrackingOptions): CallTrackingRepository {
    expireSwaps(options);

    const init = async (sessionId?: string): Promise<CallTrackingResponse> => {
        const response = await api.initCallTracking(
            options.storage.get<CachedCallTrackingResponse>(SESSION_KEY)?.swaps?.map(swap => swap.number) || [],
            new Date().toISOString(),
            sessionId,
            options.pageContext.url,
            options.pageContext.referrer,
            options.storage.get<CachedCallTrackingResponse>(SESSION_KEY)?.lastOverSwappedAt,
        )

        if (response.status !== 200) {
            error(await response.text());

            throw new Error('Received non-200 response while fetching swaps');
        }

        const payload = await response.json() as {data: CallTrackingResponse};

        return payload.data;
    };

    const existingSession = options.storage.get<CachedCallTrackingResponse>(SESSION_KEY);
    const sessionId = existingSession?.sessionId;

    const sessionInitPromise = init(sessionId);

    sessionInitPromise.then((response) => {
        const cachedResponse: CachedCallTrackingResponse = {
            sessionId: response?.sessionId,
            swaps: response.swaps,
            lastOverSwappedAt: options.storage.get<CachedCallTrackingResponse>(SESSION_KEY)?.lastOverSwappedAt || null,
        };

        if (response.lastOverSwappedAt) {
            cachedResponse.lastOverSwappedAt = response.lastOverSwappedAt;
        }

        if (response) {
            options.storage.set<CachedCallTrackingResponse>(SESSION_KEY, cachedResponse, SESSION_DURATION);
        } else {
            console.warn('DNI incomplete setup: missing sessionId or swaps from API response.');
        }
    });

    return {
        current: {
            swaps: existingSession?.swaps || [],
        },
        pending: sessionInitPromise,
    }
}
